import { useProvider } from "../lib/providersManifest";

/**
 * Iter-185.AF (T6 from audit) — AmBestBadge
 * Iter-190.I — Typography pass.
 *
 * Compact AM Best financial-strength rating badge that renders
 * inline beside the carrier name in an insurance rate table row.
 * Mirrors the NerdWallet / Insure.com / Bankrate convention of
 * surfacing a third-party authority signal next to the carrier.
 *
 * Iter-190.I — Stripped the chunky bordered-chip styling that
 * caused 3 separate badges (AM Best, J.D. Power, NAIC) to stack
 * vertically and add ~30% wasted vertical space per row. Now
 * renders as plain inline typography (label opacity-60, value
 * bolder, optional · category modifier) that composes cleanly
 * with sibling badges inside a `flex flex-wrap` parent in
 * PartnerRateTable. Visual rhythm:
 *     AM Best A++ · Superior   ·   J.D. Power 2024  873   ·   NAIC  0.65
 *
 * Data source: providers.financial_strength.am_best.rating in
 * MongoDB. Populated from public AM Best ratings (ambest.com).
 * Component renders null when no rating is set so it's safe to
 * drop into any row — no layout shift, no fallback noise.
 */
const TONE_BY_CATEGORY = {
  Superior: "text-emerald-700",
  Excellent: "text-emerald-700",
  Good: "text-amber-700",
};

export default function AmBestBadge({ slug, size = "sm" }) {
  const provider = useProvider(slug);
  const amBest = provider?.financial_strength?.am_best;
  if (!amBest?.rating) return null;

  const tone = TONE_BY_CATEGORY[amBest.category] || "text-slate-700";
  // Iter-190.I — Single typography scale via the parent wrapper.
  // `size` kept on the API for backwards-compatibility but no longer
  // changes the chip size (no chip).
  void size;

  return (
    <span
      data-testid={`am-best-badge-${slug}`}
      title={`AM Best financial strength rating: ${amBest.rating} (${amBest.category}). Source: ambest.com${amBest.verified_at ? `, verified ${new Date(amBest.verified_at).toLocaleDateString()}` : ""}.`}
      className={`inline-flex items-baseline whitespace-nowrap ${tone}`}
    >
      <span className="opacity-60">AM Best</span>
      <span data-testid={`am-best-rating-${slug}`} className="ml-1 font-display tabular-nums">
        {amBest.rating}
      </span>
      {amBest.category && (
        <span className="opacity-50 normal-case tracking-normal ml-1.5 font-medium">
          · {amBest.category}
        </span>
      )}
    </span>
  );
}
